import { FC, type ReactNode } from 'react';
import { useLarguraDoElemento } from '@/hooks/useLarguraDoElemento';
import { larguraParaRolar, modoDoRotulo, type MedidasRotulo, type ModoRotulo } from '@/utils/rotulosGrafico';

interface Props {
  altura: number;
  /** Quantas colunas (ou grupos de colunas) o gráfico tem: dias, meses. */
  categorias: number;
  medidas: MedidasRotulo;
  /** O gráfico, desenhado já sabendo como o número vai aparecer. */
  children: (modo: ModoRotulo) => ReactNode;
  /**
   * Não rola: quando o número não cabe, ele some (fica no tooltip). Para barra horizontal, em que
   * rolar para o lado esconderia as próprias barras.
   */
  semRolagem?: boolean;
  testId?: string;
}

/**
 * A área de um gráfico de colunas com o número de cada coluna à vista (03/11/2026). Mede a largura
 * que sobra na tela e escolhe: número deitado quando cabe; em pé (girado) quando só assim cabe; e,
 * quando nem em pé cabe (celular), o gráfico fica largo o bastante para o número deitado e rola
 * para o lado, com uma dica embaixo.
 */
const AreaDoGrafico: FC<Props> = ({ altura, categorias, medidas, children, semRolagem = false, testId }) => {
  const [ref, disponivel] = useLarguraDoElemento<HTMLDivElement>();
  const rolar = semRolagem ? null : larguraParaRolar(disponivel, categorias, medidas);
  const modo = modoDoRotulo(rolar ?? disponivel, categorias, medidas);
  return (
    <div>
      <div ref={ref} className={rolar ? 'overflow-x-auto' : undefined} data-testid={testId} data-rotulo={modo ?? 'nenhum'}>
        <div style={{ width: rolar ?? '100%', height: altura }}>{children(modo)}</div>
      </div>
      {rolar && (
        <p className="mt-1 flex items-center gap-1 text-xs text-content-muted" data-testid={testId && `${testId}-rolar`}>
          <span aria-hidden className="material-symbols-outlined text-[16px] leading-none">
            swipe
          </span>
          Arraste o gráfico para o lado para ver todos os números.
        </p>
      )}
    </div>
  );
};

export default AreaDoGrafico;
